import type { Player, GameRecord, PlayerStats } from '../types';

// ─────────────────────────────────────────────────
//  Player rating helpers
// ─────────────────────────────────────────────────

const SKILL_MAP: Record<string, number> = {
  Beginner:     2.0,
  Intermediate: 3.0,
  Advanced:     4.5,
  Elite:        5.5,
};

/** Returns the numeric rating for a player (DUPR preferred, then skill tier, default 3.0) */
export function playerRating(player: Player): number {
  if (player.dupr !== null) return player.dupr;
  if (player.skill) return SKILL_MAP[player.skill] ?? 3.0;
  return 3.0;
}

const SKILL_BAND: Record<string, string> = {
  Beginner:     'Beginner (1–2)',
  Intermediate: 'Intermediate (2–4)',
  Advanced:     'Advanced (4–5.5)',
  Elite:        'Elite (5.5+)',
};

/** Rating display string */
export function ratingLabel(player: Player): string {
  if (player.dupr !== null) return `DUPR ${player.dupr.toFixed(2)}`;
  if (player.skill) return SKILL_BAND[player.skill] ?? player.skill;
  return 'Unrated';
}

// ─────────────────────────────────────────────────
//  Per-player stats from game records
// ─────────────────────────────────────────────────

/**
 * Compute stats for a single player across the provided game records.
 * Pass a filtered subset for session-scoped stats.
 */
export function computePlayerStats(
  playerId: string,
  games: GameRecord[],
  players: Player[]
): PlayerStats {
  let wins = 0, losses = 0, pts = 0, gp = 0;

  for (const game of games) {
    for (const court of game.courts) {
      const inA = court.teamA.includes(playerId);
      const inB = court.teamB.includes(playerId);
      if (!inA && !inB) continue;
      if (court.scoreA == null || court.scoreB == null) continue;
      gp++;
      if (inA) {
        pts += court.scoreA;
        if (court.scoreA > court.scoreB) wins++; else losses++;
      }
      if (inB) {
        pts += court.scoreB;
        if (court.scoreB > court.scoreA) wins++; else losses++;
      }
    }
  }

  const player = players.find(p => p.id === playerId);
  const base = player ? playerRating(player) : 3.0;
  const perfScore = computePerfScore(playerId, base, { wins, pts, gp });

  return {
    playerId,
    gp,
    wins,
    losses,
    pts,
    winRate: gp > 0 ? (wins / gp) * 100 : 0,
    avgPts: gp > 0 ? pts / gp : 0,
    perfScore,
  };
}

/**
 * Blended performance score used for smart matchmaking.
 * Combines rating (weighted down as data grows) with win rate and avg points.
 */
export function computePerfScore(
  _playerId: string,
  baseRating: number,
  stats: Pick<PlayerStats, 'wins' | 'gp' | 'pts'>
): number {
  if (stats.gp === 0) return baseRating;
  const wr = stats.wins / stats.gp;
  const avgPts = stats.pts / stats.gp;
  // Weight shifts from base rating toward actual results as games accumulate (capped at 60%)
  const w = Math.min(stats.gp / 10, 0.6);
  return baseRating * (1 - w) + (baseRating + (wr - 0.5) * 2 + (avgPts - 7) * 0.05) * w;
}

// ─────────────────────────────────────────────────
//  Leaderboard builder
// ─────────────────────────────────────────────────

export function buildLeaderboard(players: Player[], games: GameRecord[]) {
  return players
    .map(p => {
      const stats = computePlayerStats(p.id, games, players);
      return { ...p, ...stats };
    })
    .sort((a, b) => {
      if (b.wins !== a.wins) return b.wins - a.wins;
      return b.pts - a.pts;
    })
    .map((row, i) => ({ ...row, rank: i + 1 }));
}
